import React, { useState, useEffect, useRef } from 'react';
import { VideoTemplate, VideoTemplateFormData } from '../types/videoTemplate';
import { toast } from "sonner";
import { X, Upload } from 'lucide-react';

interface VideoTemplateFormProps {
  onSubmit: (data: VideoTemplateFormData) => void;
  onCancel: () => void;
  loading: boolean;
  categories?: any[]; // Keep for backward compatibility but not used
  initialData?: VideoTemplate | null;
}

const VideoTemplateForm: React.FC<VideoTemplateFormProps> = ({
  onSubmit,
  onCancel,
  loading,
  initialData,
}) => {
  const [formData, setFormData] = useState<VideoTemplateFormData>({
    template_name: '',
    owner: '',
    json_prompt: '',
    ai_model: '',
    output_video: null,
  });
  const [videoPreview, setVideoPreview] = useState<string | null>(null);
  const videoInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (initialData) {
      // Handle json_prompt - if it's an object, stringify it; if it's a string, use it as is
      let jsonPromptString = '';
      if (initialData.json_prompt) {
        if (typeof initialData.json_prompt === 'object') {
          jsonPromptString = JSON.stringify(initialData.json_prompt, null, 2);
        } else {
          jsonPromptString = initialData.json_prompt;
        }
      }
      setFormData({
        template_name: initialData.template_name || '',
        owner: initialData.owner || '',
        json_prompt: jsonPromptString,
        ai_model: initialData.ai_model || '',
        output_video: null,
      });
      // Show existing video URL as preview if available (output is an array)
      if (initialData.output && initialData.output.length > 0) {
        setVideoPreview(initialData.output[0]);
      } else {
        setVideoPreview(null);
      }
    }
  }, [initialData]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleVideoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = ['video/mp4', 'video/webm', 'video/quicktime', 'video/x-msvideo', 'video/mpeg'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please upload a valid video file (MP4, WebM, MOV, AVI, MPEG)');
      return;
    }
    if (file.size > 100 * 1024 * 1024) {
      toast.error('Video file size must be less than 100MB');
      return;
    }

    setFormData((prev) => ({ ...prev, output_video: file }));
    setVideoPreview(URL.createObjectURL(file));
  };

  const handleRemoveVideo = () => {
    setFormData((prev) => ({ ...prev, output_video: null }));
    setVideoPreview(null);
    if (videoInputRef.current) {
      videoInputRef.current.value = '';
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate JSON if provided
    if (formData.json_prompt && formData.json_prompt.trim()) {
      try {
        JSON.parse(formData.json_prompt);
      } catch (error) {
        toast.error('Invalid JSON format in JSON Prompt field');
        return;
      }
    }
    
    onSubmit(formData);
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <h2 className="text-2xl font-semibold mb-6">
        {initialData ? 'Edit Video Template' : 'Create Video Template'}
      </h2>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            TEMPLATE NAME *
          </label>
          <input
            type="text"
            name="template_name"
            value={formData.template_name}
            onChange={handleChange}
            required
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-berrys-orange focus:border-transparent"
            placeholder="Enter template name (e.g., 3D Rotation)"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            OWNER
          </label>
          <input
            type="text"
            name="owner"
            value={formData.owner}
            onChange={handleChange}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-berrys-orange focus:border-transparent"
            placeholder="Enter owner name (e.g., Camera Control)"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            AI MODEL *
          </label>
          <select
            name="ai_model"
            value={formData.ai_model}
            onChange={handleChange}
            required
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-berrys-orange focus:border-transparent bg-white"
          >
            <option value="">Select an AI model</option>
            <optgroup label="Kling Models">
              <option value="kling">Kling (Base)</option>
              <option value="kling/1.6-pro">Kling 1.6 Pro</option>
              <option value="kling/2.1-master">Kling 2.1 Master</option>
              <option value="kling/2.5-turbo-pro">Kling 2.5 Turbo Pro</option>
              <option value="kling/2.6-pro">Kling 2.6 Pro</option>
              <option value="kling/o1-pro">Kling O1 Pro</option>
            </optgroup>
            <optgroup label="Seedance Models">
              <option value="seedance">Seedance (Base)</option>
              <option value="seedance/1.0">Seedance 1.0</option>
              <option value="seedance/1.0-lite">Seedance 1.0 Lite</option>
              <option value="seedance/1.0-pro">Seedance 1.0 Pro</option>
              <option value="seedance/1.5-pro">Seedance 1.5 Pro</option>
              <option value="seedance/1.5-pro-fast">Seedance 1.5 Pro Fast</option>
              <option value="seedance/lite-t2v">Seedance Lite T2V</option>
              <option value="seedance/pro-t2v">Seedance Pro T2V</option>
              <option value="seedanchor">SeedAnchor (Legacy)</option>
            </optgroup>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            JSON PROMPT (Subject only - Human) *
          </label>
          <textarea
            name="json_prompt"
            value={formData.json_prompt}
            onChange={handleChange}
            required
            rows={16}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-berrys-orange focus:border-transparent font-mono text-xs"
            placeholder='Enter JSON prompt (e.g., {"model": "kling/2.5-turbo-pro", ...})'
          />
          <p className="mt-1 text-xs text-gray-500">
            Paste the complete JSON prompt structure here. The JSON will be validated on save.
          </p>
        </div>

        {/* Output Video Upload */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            OUTPUT VIDEO {!initialData && ''}
          </label>

          {videoPreview ? (
            <div className="relative inline-block">
              <video
                src={videoPreview}
                controls
                className="w-full max-w-md h-48 object-contain rounded-lg border border-gray-300 bg-black"
              />
              <button
                type="button"
                onClick={handleRemoveVideo}
                className="absolute -top-2 -right-2 bg-red-500 hover:bg-red-600 text-white rounded-full p-1 shadow-md transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <div
              onClick={() => videoInputRef.current?.click()}
              className="w-full max-w-md border-2 border-dashed border-gray-300 rounded-lg p-8 text-center cursor-pointer hover:border-berrys-orange hover:bg-orange-50 transition-colors"
            >
              <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
              <p className="text-sm text-gray-600">Click to upload output video</p>
              <p className="text-xs text-gray-400 mt-1">MP4, WebM, MOV, AVI, MPEG (max 100MB)</p>
            </div>
          )}

          <input
            ref={videoInputRef}
            type="file"
            accept="video/mp4,video/webm,video/quicktime,video/x-msvideo,video/mpeg"
            onChange={handleVideoChange}
            className="hidden"
          />
        </div>

        <div className="flex space-x-4 pt-4">
          <button
            type="button"
            onClick={onCancel}
            className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-800 px-6 py-2 rounded-lg font-medium transition-colors"
          >
            Cancel
          </button>

          <button
            type="submit"
            disabled={loading}
            className="flex-1 bg-berrys-orange hover:bg-berrys-orange-dark text-white px-6 py-2 rounded-lg font-medium transition-colors disabled:opacity-50"
          >
            {loading
              ? initialData
                ? 'Updating...'
                : 'Creating...'
              : initialData
              ? 'Update Video Template'
              : 'Create Video Template'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default VideoTemplateForm;
